import { Set, Map, List } from 'immutable';

import {
    SET_PACKAGES,
    SET_PACKAGES_DATE,
    UPDATE_TASKS,
    SET_TASK_ASSETS,
    SET_PACKAGE_DELETED,
} from './actions';

export const PACKAGE_LIST_KEY = 'packageList';
export const PACKAGES_KEY = 'packages';
export const PACKAGES_DATE_KEY = 'packagesDate';
export const TASKS_KEY = 'tasks';
export const TASK_ASSETS_KEY = 'taskAssets';
export const PACKAGES_TYPES_KEY = 'packagesTypes';
export const PACKAGE_DELETIONS_KEY = 'packageDeletions';

/**
 * Note - a null package list means they haven't been loaded yet. An empty
 * package list means that they have been loaded but the user does not have any
 * packages assigned to them.
 */
export function packageListReducer(state = null, action) {
    switch (action.type) {
        case SET_PACKAGES:
            const newIDs = action.packages.map(pkg => pkg.get('pkg_id'));
            if (action.update) {
                // If an update action happens before the initial package load, create an empty state.
                state = state || List();
                // Update the list of packages with new ones that have just arrived.
                return state
                    .concat(newIDs)
                    .toSet()
                    .toList();
            }
            return newIDs;
    }
    return state;
}

export function packagesReducer(state = Map(), action) {
    switch (action.type) {
        case SET_PACKAGES:
            if (action.update) {
                return state.merge(Map(action.packages.map(pkg => [pkg.get('pkg_id'), pkg])));
            }
            return Map(action.packages.map(pkg => [pkg.get('pkg_id'), pkg]));
        case SET_PACKAGE_DELETED:
            const pkg = state.get(action.packageID);
            if (pkg) {
                // update the deleted_time on the package
                const newPkg = pkg.merge(Map({ deleted_time: action.deletionTime }));
                return state.merge(Map([[action.packageID, newPkg]]));
            }
            return state;
    }
    return state;
}

export function packagesDateReducer(state = null, action) {
    switch (action.type) {
        case SET_PACKAGES_DATE:
            return action.date;
    }
    return state;
}

export function packageDeletionsReducer(state = Map(), action) {
    switch (action.type) {
        case SET_PACKAGE_DELETED:
            return state.merge(Map([[action.packageID, action.deletionTime]]));
    }
    return state;
}

/**
 * Merges task items from srcTask into destTask and returns destTask.
 * Will only merge a task item if an item with the same id is not already present.
 * @param srcTask
 * @param destTask
 */
const mergeTaskItems = (srcTask, destTask) => {
    const srcTaskItems = srcTask.get('items');
    if (srcTaskItems && srcTaskItems.size > 0) {
        let destTaskItems = destTask.get('items') || List();
        for (const srcItem of srcTaskItems) {
            if (!destTaskItems.find(i => i.get('id') === srcItem.get('id'))) {
                destTaskItems = destTaskItems.push(srcItem);
            }
        }
        destTask = destTask.set('items', destTaskItems);
    }
    return destTask;
};

/**
 * Comparator for sorting task items by index (ascending)
 * @param taskItem1
 * @param taskItem2
 * @returns {number}
 */
const ascendingTaskItemIndexComparator = (taskItem1, taskItem2) => {
    const i1 = taskItem1.get('index');
    const i2 = taskItem2.get('index');
    if (i1 === undefined || i2 === undefined) {
        return 0;
    }
    return i1 - i2;
};

export function tasksReducer(state = Map(), action) {
    switch (action.type) {
        case UPDATE_TASKS:
            // Ensure that existing task items are preserved:
            const taskMap = action.tasks.map(newTask => {
                const taskId = newTask.get('task_id');
                const existingTask = state.get(taskId);
                if (existingTask) {
                    newTask = mergeTaskItems(existingTask, newTask);
                }
                // Sort task items by ascending index:
                newTask = newTask.set(
                    'items',
                    newTask.get('items').sort(ascendingTaskItemIndexComparator),
                );
                // If the task has a title, change 'Optional Homework' task titles to 'XP Boost'
                if (newTask.get('title') !== undefined) {
                    newTask = newTask.set(
                        'title',
                        newTask.get('title').replace('Optional Homework ', 'XP Boost '),
                    );
                }
                return [taskId, newTask];
            });
            return state.merge(taskMap);
    }
    return state;
}

export function taskAssetsReducer(state = Map(), action) {
    switch (action.type) {
        case SET_TASK_ASSETS:
            return state.set(action.taskId, action.assets);
    }
    return state;
}

export function packagesTypesReducer(state = Set(), action) {
    switch (action.type) {
        case SET_PACKAGES:
            const newPackageTypes = action.packages.map(pkg => pkg.get('package_type'));
            if (action.update) {
                return state.union(newPackageTypes);
            }
            return Set(newPackageTypes);
    }
    return state;
}
